import { useEffect, useState } from "react";
import { ArrowLeft, ArrowRight, CheckCircle2, Moon, Sun } from "lucide-react";
import { currentTheme, setTheme } from "../lib/theme";
import { Link, Navigate, useNavigate, useSearchParams } from "react-router-dom";
import { Logo } from "../components/Logo";
import { useAuth } from "./AuthProvider";
import { supabase } from "../lib/supabase";

// Invitation emails link to /login#invite=<email>. The fragment keeps the
// address out of server and CDN logs while letting the page prefill it.
function invitedEmailFromHash() {
  const match = window.location.hash.match(/^#invite=(.+)$/);
  if (!match) return "";
  try {
    return decodeURIComponent(match[1]).trim().toLowerCase();
  } catch {
    return match[1].trim().toLowerCase();
  }
}

export function LoginPage() {
  const { session } = useAuth();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [invited] = useState(invitedEmailFromHash);
  const [mode, setMode] = useState<"login" | "signup">(searchParams.get("mode") === "signup" && !invited ? "signup" : "login");
  const [email, setEmail] = useState(invited);
  const [businessName, setBusinessName] = useState("");
  const [token, setToken] = useState("");
  const [sent, setSent] = useState(Boolean(invited));
  const [hasCode, setHasCode] = useState(Boolean(invited));
  useEffect(() => {
    if (invited) window.history.replaceState(null, "", window.location.pathname + window.location.search);
  }, [invited]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const [bootstrapRetry, setBootstrapRetry] = useState(false);

  if (session && !busy && !bootstrapRetry) return <Navigate to="/app" replace />;

  const switchMode = (nextMode: "login" | "signup") => {
    setMode(nextMode);
    setSearchParams(nextMode === "signup" ? { mode: "signup" } : {});
    setSent(false);
    setHasCode(false);
    setToken("");
    setError("");
    setNotice("");
    setBootstrapRetry(false);
  };

  const bootstrapAccount = async (accessToken: string) => {
    const response = await fetch("/api/ads/account-bootstrap", {
      method: "POST",
      headers: { Authorization: `Bearer ${accessToken}`, "Content-Type": "application/json" },
    });
    const payload = await response.json().catch(() => ({})) as { message?: string };
    if (!response.ok) throw new Error(payload.message || "Your advertising workspace could not be created.");
  };

  const sendCode = async () => {
    if (!supabase) throw new Error("Authentication is not configured yet.");
    const normalized = email.trim().toLowerCase();
    if (!normalized) throw new Error("Enter your email address.");
    if (mode === "signup" && !businessName.trim()) throw new Error("Enter your business name.");
    const { error: sendError } = await supabase.auth.signInWithOtp({
      email: normalized,
      options: {
        shouldCreateUser: mode === "signup",
        ...(mode === "signup" ? { data: { business_name: businessName.trim() } } : {}),
      },
    });
    if (sendError) {
      // Supabase rejects sign-in for unknown emails with "Signups not allowed
      // for otp". Instead of surfacing that dead end, carry the email into the
      // signup flow so the person can create their account in one step.
      if (mode === "login" && /signups? not allowed/i.test(sendError.message || "")) {
        setMode("signup");
        setSearchParams({ mode: "signup" });
        setEmail(normalized);
        setSent(false);
        setHasCode(false);
        setToken("");
        setNotice("No account exists for this email yet. Add your business name and we’ll create your workspace.");
        return;
      }
      throw new Error(sendError.message || "The code could not be sent.");
    }
    setEmail(normalized);
    setSent(true);
    setHasCode(false);
    setNotice(`${mode === "signup" ? "An account verification" : "A secure sign-in"} code was sent. Only the newest code will work.`);
  };

  const submit = async (event: React.FormEvent) => {
    event.preventDefault();
    setBusy(true);
    setError("");
    setNotice("");
    let emailVerified = false;
    try {
      if (!sent) {
        await sendCode();
      } else {
        if (!supabase) throw new Error("Authentication is not configured yet.");
        if (token.length !== 6) throw new Error("Enter the 6-digit code.");
        // Sign-in, signup, and invitation codes all verify the same way: the
        // newest open code for this email wins.
        const attempt = await supabase.auth.verifyOtp({ email, token, type: "email" });
        if (attempt.error) throw new Error(attempt.error.message);
        const data = attempt.data;
        if (!data.session?.access_token) throw new Error("Your email was verified, but no session was created. Request a new code.");
        emailVerified = true;
        await bootstrapAccount(data.session.access_token);
        navigate("/app", { replace: true });
      }
    } catch (caught) {
      if (emailVerified) setBootstrapRetry(true);
      setError(caught instanceof Error ? caught.message : "Sign-in could not be completed.");
    } finally {
      setBusy(false);
    }
  };

  const retryBootstrap = async () => {
    if (!session?.access_token) return;
    setBusy(true);
    setError("");
    try {
      await bootstrapAccount(session.access_token);
      setBootstrapRetry(false);
      navigate("/app", { replace: true });
    } catch (caught) {
      setBootstrapRetry(true);
      setError(caught instanceof Error ? caught.message : "Your advertising workspace could not be created.");
    } finally { setBusy(false); }
  };

  const useExistingCode = () => {
    const normalized = email.trim().toLowerCase();
    if (!normalized) {
      setError("Enter your email address first.");
      return;
    }
    setEmail(normalized);
    setHasCode(true);
    setSent(true);
    setError("");
    setNotice("");
  };

  const resend = async () => {
    setBusy(true);
    setError("");
    try {
      await sendCode();
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "A new code could not be sent.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <main className="login-page">
      <section className="login-story">
        <Logo inverse />
        <div className="login-story__copy">
          <span className="eyebrow eyebrow--light">ADNUVIO</span>
          <h1>Scale campaigns.<br />Stay compliant.<br /><em>Keep moving.</em></h1>
          <div className="login-proof">
            <span><CheckCircle2 /> Campaigns and reporting in one place</span>
            <span><CheckCircle2 /> Billing and Meta assets in one workflow</span>
            <span><CheckCircle2 /> Hands-on support from your platform</span>
          </div>
        </div>
        <p><a href="mailto:hello@adnuvio.com">hello@adnuvio.com</a> · app.adnuvio.com</p>
      </section>
      <section className="login-panel">
        <button className="theme-toggle" type="button" aria-label={currentTheme() === "light" ? "Switch to dark mode" : "Switch to light mode"} onClick={() => setTheme(currentTheme() === "light" ? "dark" : "light")}>{currentTheme() === "light" ? <Moon /> : <Sun />}</button>
        <div className="login-card">
          {!sent && !bootstrapRetry && <div className="auth-mode" role="tablist" aria-label="Account access">
            <button role="tab" aria-selected={mode === "login"} className={mode === "login" ? "is-active" : ""} type="button" onClick={() => switchMode("login")}>Sign in</button>
            <button role="tab" aria-selected={mode === "signup"} className={mode === "signup" ? "is-active" : ""} type="button" onClick={() => switchMode("signup")}>Create account</button>
          </div>}
          {sent && !bootstrapRetry && <div className="code-header">
            <button type="button" onClick={() => { setSent(false); setHasCode(false); setToken(""); setNotice(""); }} aria-label="Back to email entry"><ArrowLeft /><span>{email}</span></button>
            <button type="button" onClick={() => { setSent(false); setHasCode(false); setToken(""); setNotice(""); }}>Change</button>
          </div>}
          <h2>{bootstrapRetry ? "Finish account setup" : sent ? "Enter your 6-digit code" : mode === "signup" ? "Create your client account" : "Sign in to Adnuvio"}</h2>
          <p>{bootstrapRetry ? "Your email is verified. Retry the secure ad-workspace connection to continue." : sent ? (hasCode ? <>Enter the six-digit code from your email. The code expires in 10 minutes.</> : <>We sent it to <strong>{email}</strong> a moment ago. The code expires in 10 minutes.</>) : mode === "signup" ? "Verify your work email to create your Adnuvio workspace." : "Sign in with your work email. No password required."}</p>
          {error && <div className="form-alert" role="alert">{error}</div>}
          {notice && <div className="form-alert form-alert--success">{notice}</div>}
          {bootstrapRetry ? <button className="button button--primary button--wide" type="button" onClick={() => void retryBootstrap()} disabled={busy}>{busy ? "Creating workspace…" : "Retry account setup"}</button> : <form onSubmit={submit}>
            {!sent ? (
              <>
                {mode === "signup" && <label><span>Business name</span><input value={businessName} onChange={(event) => setBusinessName(event.target.value)} placeholder="Your company" autoComplete="organization" required /></label>}
                <label><span>Work email</span><input value={email} onChange={(event) => setEmail(event.target.value)} placeholder="you@company.com" type="email" autoComplete="email" required autoFocus /></label>
              </>
            ) : (
              <label><span>One-time code</span><input className="otp-input" value={token} onChange={(event) => setToken(event.target.value.replace(/\D/g, "").slice(0, 6))} placeholder="000000" inputMode="numeric" autoComplete="one-time-code" required autoFocus /></label>
            )}
            <button className="button button--primary button--wide" type="submit" disabled={busy || (sent && token.length !== 6)}>{busy ? (sent ? mode === "signup" ? "Creating workspace…" : "Signing in…" : "Sending code…") : sent ? (mode === "signup" ? "Verify and create account" : "Verify and sign in") : mode === "signup" ? "Email me a verification code" : "Email me a sign-in code"}{!busy && <ArrowRight />}</button>
          </form>}
          {!sent && !bootstrapRetry && mode === "login" && <div className="login-actions"><button type="button" onClick={useExistingCode} disabled={busy}>Already have a code? Enter it</button></div>}
          {sent && <div className="login-actions login-actions--code"><span>Didn’t get the code?</span><button type="button" onClick={resend} disabled={busy}>{hasCode ? "Email me a fresh code" : "Resend code"}</button></div>}
          {sent && <p className="login-switch">Still stuck? <a href="mailto:hello@adnuvio.com">Contact support</a></p>}
          {!sent && !bootstrapRetry && (mode === "login"
            ? <p className="login-switch">New to Adnuvio? <button type="button" onClick={() => switchMode("signup")}>Create an account</button></p>
            : <p className="login-switch">Already have an account? <button type="button" onClick={() => switchMode("login")}>Sign in</button></p>)}
          {!sent && <p className="login-legal">By continuing, you agree to the platform’s <Link to="/terms">Ads Terms</Link> and acknowledge its privacy policy.</p>}
        </div>
      </section>
    </main>
  );
}
